/**
 * iter235 — Bloc Gardiennage réutilisable (fiche client, fiche OR).
 *
 * `compact` (OR) : une ligne discrète « 🛞 4 pneus hiver en gardiennage » +
 * l'alerte « Gardiennage Été 2026 à facturer – 60 CHF » si une saison échue
 * n'est pas encore reprise sur un document.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { CircleDot, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { TYPE_LABEL, TYPE_CLASS, badgesOf, fmtChf } from "@/lib/gardiennage";

export default function GardiennageBlock({ clientId, vehiculeId, compact = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!clientId && !vehiculeId) return;
    const params = vehiculeId ? { vehicule_id: vehiculeId } : { client_id: clientId };
    api.get("/gardiennages", { params })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [clientId, vehiculeId]);

  if (items.length === 0) return null;
  const stockes = items.filter((g) => (g.statut || "stocke") === "stocke");
  const aFacturer = items.filter((g) => g.facturation_statut === "a_facturer" && !g.facture_id);

  if (compact) {
    if (stockes.length === 0 && aFacturer.length === 0) return null;
    return (
      <div className="mt-3 space-y-1.5" data-testid="or-gardiennage-info">
        {stockes.map((g) => (
          <div key={g.id} className="text-xs text-gray-700 inline-flex items-center gap-1.5 mr-3">
            <CircleDot className="w-3.5 h-3.5 text-[#52a675]" />
            <span>
              {g.quantite} pneus {TYPE_LABEL[g.type_pneus]?.toLowerCase()} en gardiennage
              <span className="text-gray-400"> · {g.saison_label}</span>
            </span>
            {g.a_monter && (
              <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-800 border-orange-300">
                à monter
              </Badge>
            )}
          </div>
        ))}
        {aFacturer.map((g) => (
          <button
            key={`f-${g.id}`}
            onClick={() => navigate("/gardiennage")}
            className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1 hover:bg-red-100"
            data-testid={`or-gardiennage-a-facturer-${g.id}`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Gardiennage {g.saison_label} à facturer – {fmtChf(g.tarif)}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="p-5 mt-4 bg-white border border-gray-200 rounded-lg shadow-sm" data-testid="gardiennage-block">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
        <CircleDot className="w-3.5 h-3.5" /> Gardiennage pneus ({items.length})
      </div>
      <div className="space-y-2">
        {items.map((g) => (
          <div
            key={g.id}
            className="flex flex-wrap items-center gap-2 text-sm border-b border-gray-50 pb-2 last:border-0 last:pb-0"
            data-testid={`gardiennage-row-${g.id}`}
          >
            <span className="font-mono font-semibold text-gray-800">{g.plaque}</span>
            <Badge variant="outline" className={`text-[10px] ${TYPE_CLASS[g.type_pneus]}`}>
              {g.quantite} {TYPE_LABEL[g.type_pneus]?.toLowerCase()}
            </Badge>
            <span className="text-xs text-gray-600">{g.saison_label}</span>
            <span className="text-xs text-gray-500">· {fmtChf(g.tarif)}</span>
            <div className="flex flex-wrap gap-1">
              {badgesOf(g).map((b) => (
                <Badge key={b.key} variant="outline" className={`text-[10px] ${b.cls}`}>{b.label}</Badge>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={() => navigate("/gardiennage")}
        className="text-xs text-[#3d8a5c] hover:underline mt-2"
        data-testid="gardiennage-block-open"
      >
        Ouvrir le module Gardiennage →
      </button>
    </div>
  );
}
